/**
 * Application Insights configuration and telemetry utilities for React frontend
 */

import { ApplicationInsights, SeverityLevel, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';

interface TelemetryConfig {
  connectionString?: string;
  instrumentationKey?: string;
  environment: string;
  version: string;
}

interface CustomProperties {
  [key: string]: string;
}

interface CustomMeasurements {
  [key: string]: number;
}

class TelemetryService {
  private appInsights: ApplicationInsights;
  private reactPlugin: ReactPlugin;
  private browserHistory: any;
  private isInitialized: boolean = false;
  private config: TelemetryConfig;

  constructor() {
    this.browserHistory = createBrowserHistory();
    this.reactPlugin = new ReactPlugin();
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING,
        instrumentationKey: process.env.REACT_APP_APPLICATIONINSIGHTS_INSTRUMENTATION_KEY,
        extensions: [this.reactPlugin],
        extensionConfig: {
          [this.reactPlugin.identifier]: { history: this.browserHistory }
        },
        enableAutoRouteTracking: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxPerfTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
        disableFetchTracking: false,
        enableCorsCorrelation: true,
        distributedTracingMode: 2, // DistributedTracingModes.AI_AND_W3C
        maxBatchInterval: 15000,
        disableExceptionTracking: false,
        disableTelemetry: false,
        loggingLevelConsole: 1,
        loggingLevelTelemetry: 1,
        diagnosticLogInterval: 10000
      }
    });

    this.config = {
      connectionString: process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING,
      instrumentationKey: process.env.REACT_APP_APPLICATIONINSIGHTS_INSTRUMENTATION_KEY,
      environment: process.env.NODE_ENV || 'development',
      version: '4.0.0'
    };

    this.initialize();
  }

  private initialize(): void {
    try {
      if (this.config.connectionString || this.config.instrumentationKey) {
        this.appInsights.loadAppInsights();
        
        // Set user context
        this.appInsights.setAuthenticatedUserContext('anonymous-user', 'assessment-tool');
        
        // Set application context
        this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
          envelope.tags = envelope.tags || {};
          envelope.tags['ai.application.ver'] = this.config.version;
          envelope.tags['ai.cloud.role'] = 'frontend-react';
          envelope.tags['ai.cloud.roleInstance'] = 'frontend-instance';
          
          // Add custom properties to all telemetry
          envelope.data = envelope.data || {};
          if (envelope.data.baseData) {
            envelope.data.baseData.properties = envelope.data.baseData.properties || {};
            envelope.data.baseData.properties['environment'] = this.config.environment;
            envelope.data.baseData.properties['version'] = this.config.version;
          }
          
          return true;
        });

        this.isInitialized = true;
        console.log('✅ Application Insights initialized successfully');
        
        // Track application start
        this.trackEvent('ApplicationStarted', {
          version: this.config.version,
          environment: this.config.environment,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });

      } else {
        console.warn('❌ Application Insights not configured - missing connection string or instrumentation key');
      }
    } catch (error) {
      console.error('❌ Failed to initialize Application Insights:', error);
      this.isInitialized = false;
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(name: string, properties?: CustomProperties, measurements?: CustomMeasurements): void {
    if (this.isInitialized) {
      try {
        this.appInsights.trackEvent({
          name,
          properties: {
            ...properties,
            timestamp: new Date().toISOString()
          },
          measurements
        });
      } catch (error) {
        console.error(`Failed to track event ${name}:`, error);
      }
    }
  }

  /**
   * Track page view
   */
  trackPageView(name?: string, url?: string, properties?: CustomProperties): void {
    if (this.isInitialized) {
      try {
        this.appInsights.trackPageView({
          name: name || document.title,
          uri: url || window.location.href,
          properties: {
            ...properties,
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error('Failed to track page view:', error);
      }
    }
  }

  /**
   * Track an exception
   */
  trackException(error: Error, properties?: CustomProperties): void {
    if (this.isInitialized) {
      try {
        this.appInsights.trackException({
          exception: error,
          severityLevel: SeverityLevel.Error,
          properties: {
            ...properties,
            timestamp: new Date().toISOString(),
            errorMessage: error.message,
            errorStack: error.stack || 'No stack trace available'
          }
        });
      } catch (trackingError) {
        console.error('Failed to track exception:', trackingError);
      }
    }
  }

  /**
   * Track a custom metric
   */
  trackMetric(name: string, average: number, properties?: CustomProperties): void {
    if (this.isInitialized) {
      try {
        this.appInsights.trackMetric({
          name,
          average,
          properties: {
            ...properties,
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error(`Failed to track metric ${name}:`, error);
      }
    }
  }

  /**
   * Track dependency call (API calls, etc.)
   */
  trackDependency(name: string, command: string, duration: number, success: boolean, properties?: CustomProperties): void {
    if (this.isInitialized) {
      try {
        this.appInsights.trackDependencyData({
          id: `${name}-${Date.now()}`,
          name,
          data: command,
          duration,
          success,
          responseCode: success ? 200 : 500,
          properties: {
            ...properties,
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error(`Failed to track dependency ${name}:`, error);
      }
    }
  }

  /**
   * Track assessment-specific events
   */
  trackAssessmentEvent(eventType: string, category?: string, domain?: string, properties?: CustomProperties): void {
    this.trackEvent(`Assessment${eventType}`, {
      category: category || 'unknown',
      domain: domain || 'unknown',
      ...properties
    });
  }

  /**
   * Track user interactions
   */
  trackUserAction(action: string, component: string, properties?: CustomProperties): void {
    this.trackEvent('UserAction', {
      action,
      component,
      ...properties
    });
  }

  /**
   * Track performance metrics
   */
  trackPerformance(name: string, duration: number, properties?: CustomProperties): void {
    this.trackMetric(`Performance_${name}`, duration, {
      metric_type: 'performance',
      ...properties
    });
  }

  /**
   * Set user context
   */
  setUser(userId: string, accountId?: string): void {
    if (this.isInitialized) {
      try {
        this.appInsights.setAuthenticatedUserContext(userId, accountId);
      } catch (error) {
        console.error('Failed to set user context:', error);
      }
    }
  }

  /**
   * Clear user context
   */
  clearUser(): void {
    if (this.isInitialized) {
      try {
        this.appInsights.clearAuthenticatedUserContext();
      } catch (error) {
        console.error('Failed to clear user context:', error);
      }
    }
  }

  /**
   * Flush telemetry (force send)
   */
  flush(): void {
    if (this.isInitialized) {
      try {
        this.appInsights.flush();
      } catch (error) {
        console.error('Failed to flush telemetry:', error);
      }
    }
  }

  /**
   * Get the React plugin for use with React components
   */
  getReactPlugin(): ReactPlugin {
    return this.reactPlugin;
  }

  /**
   * Get the browser history for routing
   */
  getBrowserHistory(): any {
    return this.browserHistory;
  }

  /**
   * Check if Application Insights is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }
}

// Create singleton instance
const telemetryService = new TelemetryService();

// Export telemetry service and hooks
export { telemetryService };
export default telemetryService;

// Performance monitoring hook
export const usePerformanceTracking = (componentName: string) => {
  return {
    trackRender: (duration: number) => {
      telemetryService.trackPerformance(`Render_${componentName}`, duration);
    },
    trackAction: (action: string, properties?: CustomProperties) => {
      telemetryService.trackUserAction(action, componentName, properties);
    },
    trackError: (error: Error, properties?: CustomProperties) => {
      telemetryService.trackException(error, { component: componentName, ...properties });
    }
  };
};
